import { useNavigate } from 'react-router-dom';
import { RefreshCw } from 'lucide-react';
import { Button, Chip } from '@nyce/admin-kit/ui';
import { EmptyState, ErrorBox, Page } from '../../components/common';
import { identityPath } from '../../identity/session';
import { IssueList, Kpi, joinParts } from './shared';
import { identityIssues, useIdentitySummary, type TargetSummary } from './identity';
import { authUsageIssues, useAuthUsage } from './authUsage';
import { MigrationSection } from './MigrationSection';

function EnvironmentSection({ s }: { s: TargetSummary }) {
  const navigate = useNavigate();
  const link = (path: string) => identityPath(path, s.target.id);
  const t = s.tenants;
  const scim = s.scim;
  return (
    <section className="oph-dash-section" aria-label={s.target.label} data-target={s.target.id}>
      <div className="oph-dash-section-head">
        <h2>
          {s.target.label} <span className="nyk-ui-hint">{s.target.authUrl}</span>
        </h2>
        <Chip size="sm" className={s.signedIn ? 'nyk-tone-green' : 'nyk-tone-grey'}>
          {s.signedIn ? 'signed in' : 'signed out'}
        </Chip>
      </div>
      {!s.signedIn ? (
        <EmptyState
          card
          title={`Not signed in to ${s.target.label}`}
          hint={
            s.error && s.error !== 'not signed in' ? s.error : 'Open a page of this environment to sign in.'
          }
          action={
            <Button size="sm" variant="ghost" onClick={() => navigate(link('/identity/tenants'))}>
              Sign in
            </Button>
          }
        />
      ) : (
        <>
          <ErrorBox error={s.error} />
          <div className="nyk-kpis">
            {t && (
              <>
                <Kpi
                  label="Tenants"
                  value={`${t.active} / ${t.total}`}
                  sub={joinParts([
                    'active',
                    t.suspended > 0 && `${t.suspended} suspended`,
                    t.deleted > 0 && `${t.deleted} deleted`,
                  ])}
                  tone={t.noProvider.length ? 'red' : undefined}
                  to={link('/identity/tenants')}
                />
                <Kpi label="Customers" value={s.customers ?? '—'} to={link('/identity/customers')} />
                <Kpi label="Workserver tenants" value={t.workserver} sub="sign in at a WMS installation" />
                <Kpi
                  label="Database tenants"
                  value={t.database}
                  sub={scim ? `${scim.connected} connected to SCIM` : 'local users'}
                />
              </>
            )}
            {scim && (
              <Kpi
                label="SCIM endpoints"
                value={scim.total}
                sub={joinParts([
                  `${scim.auth} → AUTH`,
                  `${scim.nycelogic} → WMS`,
                  scim.traced.length > 0 && `${scim.traced.length} traced`,
                ])}
                tone={
                  scim.orphans.length
                    ? 'red'
                    : scim.traced.length || scim.misrouted.length
                      ? 'amber'
                      : undefined
                }
                to={link('/identity/scim')}
              />
            )}
          </div>
          {s.scimError && <ErrorBox error={`SCIM gateway: ${s.scimError}`} />}
        </>
      )}
    </section>
  );
}

/** Identity dashboard: tenants, customers and SCIM per AUTH environment, and what looks wrong there. */
export function IdentityDashboardPage() {
  const res = useIdentitySummary();
  const usage = useAuthUsage();
  const list = res.data ?? [];
  return (
    <Page
      title="Identity"
      description="AUTH tenants and customers and the SCIM gateways, per environment, and the migration of services to AUTH. Cached for 2 minutes (auth usage 1 minute)."
      actions={
        <Button
          size="sm"
          variant="ghost"
          icon={<RefreshCw size={14} />}
          loading={res.loading || usage.loading}
          onClick={() => {
            res.reload();
            usage.reload();
          }}
        >
          Refresh
        </Button>
      }
    >
      <ErrorBox error={res.error} />
      {!res.enabled && (
        <EmptyState card title="No identity access" hint="Needs ophub:auth:read or ophub:scim:read." />
      )}
      {res.enabled && !res.data && res.loading && <div className="oph-dash-skeleton" aria-busy="true" />}
      {res.data && (
        <div className={res.loading ? 'oph-dash-stale' : undefined}>
          <IssueList
            issues={[...identityIssues(list), ...authUsageIssues(usage.data ?? [])]}
            title="Identity issues"
            clear="No identity issues."
          />
          {list.map((s) => (
            <EnvironmentSection key={s.target.id} s={s} />
          ))}
          {!list.length && <EmptyState card title="No AUTH environments configured" />}
          <MigrationSection usage={usage} />
        </div>
      )}
    </Page>
  );
}
